/**
 * Confirm Page
 * Booking summary and confirmation
 */

import {
  t,
  formatDate,
  formatCurrency,
  translateRoomType,
  translateRatePlan,
  getCurrentLanguage,
} from '../i18n';
import { Header, Card, SummaryRow, StepIndicator, FloatingNav } from '../components';
import { getState, setBooking, navigateTo, setLoading, setError, calculateNights } from '../store';
import { createBooking, saveUserProfile } from '../api';
import { config } from '../config';
import type { BookingRequest } from '../types';

/**
 * Render confirm page
 */
export function renderConfirmPage(): string {
  const state = getState();
  const { search, selectedRoom, guestInfo, bookingType } = state;

  if (!selectedRoom || !guestInfo) {
    navigateTo('search');
    return '';
  }

  const isSpa = bookingType === 'SPA';
  const isRest = bookingType === 'RESTAURANT';

  const nights = search ? calculateNights(search.checkIn, search.checkOut) : 1;
  const totalRateWithExtras = selectedRoom.totalRate + (state.extraCharges?.totalExtraCharge || 0);

  let detailsCardHtml = '';

  if (isSpa && state.spaDetails) {
    detailsCardHtml = `
      <div class="summary-section">
        <h4>🌿 รายละเอียดสปา (Spa Details)</h4>
        ${SummaryRow('ทรีตเมนต์', state.spaDetails.treatmentName?.th || selectedRoom.roomTypeName)}
        ${SummaryRow('วันที่จอง', state.spaDetails.bookingDate)}
        ${SummaryRow('รอบเวลา', `${state.spaDetails.timeSlot} น. (${state.spaDetails.durationMinutes} นาที)`)}
        ${SummaryRow('จำนวนผู้รับบริการ', `${state.spaDetails.pax} ท่าน`)}
        ${state.spaDetails.roomNumber ? SummaryRow('หมายเลขห้องพัก', `ห้อง ${state.spaDetails.roomNumber}`) : ''}
      </div>
    `;
  } else if (isRest && state.restaurantDetails) {
    detailsCardHtml = `
      <div class="summary-section">
        <h4>🍽️ รายละเอียดการจองโต๊ะอาหาร (Restaurant Details)</h4>
        ${SummaryRow('โซนที่นั่ง', state.restaurantDetails.seatingArea || 'Standard')}
        ${SummaryRow('วันที่จอง', state.restaurantDetails.bookingDate)}
        ${SummaryRow('รอบเวลา', `${state.restaurantDetails.timeSlot} น.`)}
        ${SummaryRow('จำนวนที่นั่ง', `${state.restaurantDetails.partySize} ท่าน`)}
        ${state.restaurantDetails.dietaryRestrictions ? SummaryRow('ความต้องการพิเศษ', state.restaurantDetails.dietaryRestrictions) : ''}
        ${state.restaurantDetails.roomNumber ? SummaryRow('หมายเลขห้องพัก', `ห้อง ${state.restaurantDetails.roomNumber}`) : ''}
      </div>
    `;
  } else {
    detailsCardHtml = `
      <div class="summary-section">
        <h4>📅 ${t('checkIn')} / ${t('checkOut')}</h4>
        ${SummaryRow(t('checkIn'), formatDate(search?.checkIn || ''))}
        ${SummaryRow(t('checkOut'), formatDate(search?.checkOut || ''))}
        ${SummaryRow(t('nights'), `${nights} ${t('nights')}`)}
      </div>
      
      <div class="summary-section">
        <h4>🛏️ ${t('roomType')}</h4>
        ${SummaryRow(t('roomType'), translateRoomType(selectedRoom.roomTypeCode, selectedRoom.roomTypeName))}
        ${SummaryRow('Rate Plan', translateRatePlan(selectedRoom.ratePlanCode, selectedRoom.ratePlanName))}
        ${SummaryRow(t('rooms'), (search?.rooms || 1).toString())}
        ${SummaryRow(`${t('adults')} / ${t('children')}`, `${search?.adults || 1} / ${search?.children || 0}`)}
      </div>
    `;
  }

  return `
    ${Header(t('confirmTitle'), false)}
    ${StepIndicator(4)}
    
    <div class="page-content" id="confirm-content">
      ${Card(`
        <h3 class="card-title">${t('bookingSummary')}</h3>
        
        ${detailsCardHtml}
        
        <div class="summary-section">
          <h4>👤 ${t('guestName')}</h4>
          ${SummaryRow(t('guestName'), `${guestInfo.firstName} ${guestInfo.lastName}`)}
          ${SummaryRow(t('email'), guestInfo.email)}
          ${SummaryRow(t('phone'), guestInfo.phone)}
          ${guestInfo.specialRequests ? SummaryRow(t('specialRequests'), guestInfo.specialRequests) : ''}
        </div>
      `)}
      
      ${Card(
        `
        <div class="price-breakdown">
          ${
            selectedRoom.numberOfRooms > 1
              ? `${SummaryRow(
                  `${t('roomRate')} (${selectedRoom.numberOfRooms} ${t('rooms')})`,
                  `${selectedRoom.numberOfRooms} × ${formatCurrency(selectedRoom.ratePerRoom, selectedRoom.currency)} = ${formatCurrency(selectedRoom.totalRate, selectedRoom.currency)}`
                )}`
              : SummaryRow(
                  t('roomRate'),
                  formatCurrency(selectedRoom.totalRate, selectedRoom.currency)
                )
          }
          ${
            state.extraCharges
              ? `
            ${SummaryRow(
              t('extraCharges') || 'ค่าบริการเพิ่ม',
              formatCurrency(state.extraCharges.totalExtraCharge, selectedRoom.currency),
              false
            )}
          `
              : ''
          }
        </div>
        <div class="total-section">
          <div class="total-label">${t('totalAmount')}</div>
          <div class="total-amount">${formatCurrency(totalRateWithExtras, selectedRoom.currency)}</div>
        </div>
      `,
        'total-card'
      )}
      
      ${selectedRoom.isRefundable === false ? `
      <div style="margin:0 0 12px; padding:12px 14px; background:#fee2e2; border:1px solid #fca5a5; border-radius:8px; font-size:13px; color:#991b1b; text-align:center;">
        <div style="font-weight:700; margin-bottom:4px;">${t('nonRefundable')}</div>
        <div>${t('nonRefundableWarning')}</div>
      </div>
      ` : ''}
      <div class="form-actions" style="margin-top: 16px;">
        <button 
          type="button" 
          class="btn btn-primary btn-block btn-lg"
          onclick="window.confirmBooking()"
        >
          ✓ ${t('confirmBooking')}
        </button>
      </div>
    </div>

    <!-- Floating scroller (Top / Bottom) -->
    <div id="confirm-scroller" style="position:fixed; right:16px; bottom:24px; display:flex; flex-direction:column; gap:8px; z-index:2000;">
      <button id="confirm-scroll-to-top" aria-label="Scroll to top" style="display:none; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▲</button>
      <button id="confirm-scroll-to-bottom" aria-label="Scroll to bottom" style="display:flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▼</button>
    </div>

    ${FloatingNav()}
  `;
}

/**
 * Initialize confirm page event handlers
 */
export function initConfirmPage(): void {
  // Back button handler
  window.handleBack = () => {
    navigateTo('guest-info');
  };

  // Confirm booking handler
  window.confirmBooking = async () => {
    // Check if we're in edit mode
    const editBookingId = sessionStorage.getItem('editBookingId');
    const isEditMode = !!editBookingId;

    // Get fresh state when confirming
    const state = getState();
    const { search, selectedRoom, guestInfo, user, extraCharges } = state;

    console.log('Booking state:', {
      search,
      selectedRoom,
      guestInfo,
      user,
      extraCharges,
      isEditMode,
      editBookingId,
    });

    const isSpaOrRest = state.bookingType === 'SPA' || state.bookingType === 'RESTAURANT';

    if (!selectedRoom || (!isSpaOrRest && !search) || !guestInfo) {
      console.error('Missing required data:', {
        search: !!search,
        selectedRoom: !!selectedRoom,
        guestInfo: !!guestInfo,
      });
      setError(t('errorGeneric'));
      return;
    }

    setLoading(true);

    try {
      // Calculate total rate including extra charges
      const totalRateWithExtras = selectedRoom.totalRate + (extraCharges?.totalExtraCharge || 0);

      if (isEditMode && editBookingId) {
        // Update existing booking via PUT
        console.log('[Confirm] Updating booking:', editBookingId);

        const { apiFetch } = await import('../api');
        const { getTenantId } = await import('../config');
        const tenantId = getTenantId();

        const updateData = {
          checkIn: search?.checkIn || '',
          checkOut: search?.checkOut || '',
          adults: search?.adults || 1,
          children: search?.children || 0,
          rooms: search?.rooms || 1,
          roomTypeCode: selectedRoom.roomTypeCode,
          roomTypeName: translateRoomType(selectedRoom.roomTypeCode, selectedRoom.roomTypeName),
          ratePlanCode: selectedRoom.ratePlanCode,
          ratePlanName: translateRatePlan(selectedRoom.ratePlanCode, selectedRoom.ratePlanName),
          totalRate: totalRateWithExtras,
          ratePerRoom: selectedRoom.ratePerRoom,
          totalBeforeTax: selectedRoom.totalBeforeTax,
          currency: selectedRoom.currency,
          guestFirstName: guestInfo.firstName,
          guestLastName: guestInfo.lastName,
          guestEmail: guestInfo.email,
          guestPhone: guestInfo.phone,
          specialRequests: guestInfo.specialRequests || null,
          language: getCurrentLanguage(),
          extraCharges: extraCharges || undefined,
        };

        const result = await apiFetch<{ success: boolean; data: any; error?: string }>(
          `/api/${tenantId}/bookings/${editBookingId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateData),
          }
        );

        if (result.success && result.data) {
          // Clear edit mode data
          sessionStorage.removeItem('editBookingId');
          sessionStorage.removeItem('editBookingData');

          setBooking(result.data);
          navigateTo('success');
        } else {
          const errorMsg = result.error || t('errorUpdatingBooking');
          alert(errorMsg);
          setError(errorMsg);
        }
      } else {
        // Create new booking via POST
        console.log('[Confirm] Creating new booking');

        const todayStr = new Date().toISOString().split('T')[0];
        const bookingType = state.bookingType || 'ROOM';

        const bookingRequest: BookingRequest = {
          tenantId: config.tenantId,
          lineUserId: user?.userId || 'demo-user-001',
          bookingType,
          spaDetails: state.spaDetails,
          restaurantDetails: state.restaurantDetails,
          checkIn: bookingType === 'SPA' ? state.spaDetails?.bookingDate : (bookingType === 'RESTAURANT' ? state.restaurantDetails?.bookingDate : (search?.checkIn || todayStr)),
          checkOut: bookingType === 'SPA' ? state.spaDetails?.bookingDate : (bookingType === 'RESTAURANT' ? state.restaurantDetails?.bookingDate : (search?.checkOut || todayStr)),
          adults: bookingType === 'SPA' ? (state.spaDetails?.pax || 1) : (bookingType === 'RESTAURANT' ? (state.restaurantDetails?.partySize || 1) : (search?.adults || 1)),
          children: search?.children || 0,
          rooms: search?.rooms || 1,
          roomTypeCode: selectedRoom.roomTypeCode,
          roomTypeName: translateRoomType(selectedRoom.roomTypeCode, selectedRoom.roomTypeName),
          ratePlanCode: selectedRoom.ratePlanCode,
          ratePlanName: translateRatePlan(selectedRoom.ratePlanCode, selectedRoom.ratePlanName),
          totalRate: totalRateWithExtras,
          ratePerRoom: selectedRoom.ratePerRoom,
          totalBeforeTax: selectedRoom.totalBeforeTax,
          currency: selectedRoom.currency,
          guest: guestInfo,
          language: getCurrentLanguage(),
          extraCharges: extraCharges || undefined,
          isRefundable: selectedRoom.isRefundable,
        };

        console.log('Sending booking request:', bookingRequest);

        const confirmation = await createBooking(bookingRequest);
        const finalBooking = {
          ...confirmation,
          bookingType: state.bookingType,
          spaDetails: confirmation.spaDetails || state.spaDetails,
          restaurantDetails: confirmation.restaurantDetails || state.restaurantDetails,
        };
        setBooking(finalBooking);

        // Save guest info to Firestore for auto-fill next time
        if (user?.userId) {
          console.log('[Confirm] Saving user profile to Firestore...');
          saveUserProfile(user.userId, {
            firstName: guestInfo.firstName,
            lastName: guestInfo.lastName,
            email: guestInfo.email,
            phone: guestInfo.phone,
            title: guestInfo.title,
            nationality: guestInfo.nationality,
            idType: guestInfo.idType,
            idNumber: guestInfo.idNumber,
          })
            .then(success => {
              if (success) {
                console.log('[Confirm] User profile saved successfully');
              } else {
                console.warn('[Confirm] Failed to save user profile');
              }
            })
            .catch(err => {
              console.error('[Confirm] Error saving user profile:', err);
            });
        }

        // For Pay at Venue (Spa, Restaurant, Pay at Hotel), navigate directly to success page
        if (bookingType === 'SPA' || bookingType === 'RESTAURANT' || confirmation?.paymentStatus === 'pay_at_hotel') {
          navigateTo('success');
        } else {
          navigateTo('payment');
        }
      }
    } catch (error) {
      console.error('Booking operation failed:', error);
      setError(t('errorBookingFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Expose forward action to footer (calls confirmBooking)
  window.handleForward = () => {
    if (window.confirmBooking) {
      window.confirmBooking();
    }
  };

  // Floating scroller (Top / Bottom) for Confirm page
  try {
    const scrollerTop = document.getElementById(
      'confirm-scroll-to-top'
    ) as HTMLButtonElement | null;
    const scrollerBottom = document.getElementById(
      'confirm-scroll-to-bottom'
    ) as HTMLButtonElement | null;
    const listEl = document.getElementById('confirm-content');
    if (listEl) {
      function updateScrollerVisibility() {
        if (!scrollerTop || !scrollerBottom || !listEl) return;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        scrollerTop.style.display = scrollTop > 150 ? 'flex' : 'none';
        const nearBottom =
          window.innerHeight + scrollTop >= document.documentElement.scrollHeight - 150;
        scrollerBottom.style.display = nearBottom ? 'none' : 'flex';
      }

      scrollerTop?.addEventListener('click', () => {
        // Scroll to absolute top of the page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      scrollerBottom?.addEventListener('click', () => {
        listEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
        window.scrollTo({
          top: document.documentElement.scrollHeight - window.innerHeight,
          behavior: 'smooth',
        });
      });

      window.addEventListener('scroll', updateScrollerVisibility);
      window.addEventListener('resize', updateScrollerVisibility);
      // initial
      updateScrollerVisibility();
    }
  } catch (e) {
    console.error('[Confirm] Failed to init scroller', e);
  }
}

// Extend window for global handlers
declare global {
  interface Window {
    confirmBooking: () => void;
    handleForward?: () => void;
  }
}
